<template>
  <section id="kjoperrapport-generator" class="content white large-padding">
    <form class="hide-for-print" @submit.prevent="handleSubmit" spellcheck="false">
      <TheLoader :active="loading" :update="true"/>

      <fieldset style="margin: 0;">
        <div class="grid-x grid-padding-x">
          <TheAutoCompleteInput v-model.trim="form.code"
            ref="auksjon_input"
            containerClassName="cell large-2"
            name="Auksjon"
            :placeholder="'Søk auksjons kode'"
            :label="'Auksjonskode'"
            :onInputCallback="onAuksjonSearchInput"
            :onSelectCallback="onAuksjonSearchSelected"
            :debounce="750"
            :minquery="2"
          >
            <div slot="item" slot-scope="props">
              <strong>{{props.item.Auksjon}}</strong>, {{props.item.Beskrivelse}}
              <span v-if="props.item.Auksjon_dato">
                ({{ props.item.Auksjon_dato | avantidate }})
              </span>
            </div>
          </TheAutoCompleteInput>

          <div class="cell large-1">
            <label>Antall objekter (min)</label>
            <div class="select-container">
              <input
                v-model.number="form.minCount"
                type="number"
                min="1"
                name="minCount"
                @change="clampCount"
              />
              <span class="glyph stepper-arrows">
                <svg viewBox="0 0 44 44"><use xlink:href="/static/sprites.svg#glyph-input-select"></use></svg>
                <button type="button" class="stepper-up" @click="incrementCount" tabindex="-1"></button>
                <button type="button" class="stepper-down" @click="decrementCount" :disabled="form.minCount <= 1" tabindex="-1"></button>
              </span>
            </div>
          </div>

          <div class="cell auto">
            <label>&nbsp;</label>
            <a v-if="isValidForSubmit && !loading" href="javascript:;" @click="handleSubmit()" class="button primary submit tiny">Ok</a>
            <a v-else-if="loading" href="javascript:;" class="button primary submit tiny loading">Ok <span class="loader-spinner"></span></a>
            <a v-else href="javascript:;" class="button primary submit tiny" disabled>Ok</a>
          </div>
        </div>
      </fieldset>
    </form>

    <div class="hide-for-print sidebar-padding" v-if="customers.length > 0 && !loading" style="padding-top: 0.75rem;">
      <a href="javascript:;" @click="handlePrint" class="button secondary tiny">Skriv ut</a>
    </div>

    <div class="content-container flex-column" v-if="searchDone">
      <div class="sidebar-padding hide-for-print" style="padding-top: 1rem;">
        <span v-if="customers.length === 0">Fant ingen kunder med det valgte antallet objekter.</span>
        <span v-else-if="customers.length === 1">Fant <strong>1</strong> kunde</span>
        <span v-else>Fant <strong>{{customers.length}}</strong> kunder</span>
      </div>

      <div
        v-for="(customer) in customers"
        :key="customer.Kundenr"
        class="customer-block sidebar-padding"
        style="margin-bottom: 1.5rem;"
      >
        <div class="customer-header" style="background: #f4f4f4; padding: 0.5rem 0.75rem; margin-bottom: 0.25rem;">
          <strong>{{customer.parsed_name}}</strong>
          <span style="margin-left: 0.5rem; color: #666;">(nr. {{customer.Kundenr}})</span>
          <span style="margin-left: 1rem;">{{customer.AntallObjekter}} objekter</span>
          <span v-if="customer.VinfraktName" style="margin-left: 1rem; color: #444;">{{ customer.VinfraktName }}</span>
          <span v-else style="margin-left: 1rem; color: #999; font-style: italic;">Hentested ikke valgt</span>
          <span v-if="customer.Mobil || customer.Tlfprivat" style="margin-left: 1rem; color: #444;">
            {{customer.Mobil || customer.Tlfprivat}}
          </span>
          <span v-if="customer.Adresse1" style="margin-left: 1rem; color: #444;">
            {{customer.Adresse1}}<template v-if="customer.Postnr">, {{customer.Postnr}}</template><template v-if="customer.Poststed"> {{customer.Poststed}}</template>
          </span>
        </div>

        <table class="large compressed" style="width: 100%;">
          <thead>
            <tr>
              <th style="width: 120px;">Objektnr</th>
              <th>Tittel</th>
              <th style="width: 80px; text-align: right;">Antall</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="obj in customer.objects" :key="obj.Objektnr">
              <td>{{obj.Objektnr}}</td>
              <td>{{obj.Katalogtekst1}}</td>
              <td style="text-align: right;">{{obj.Antall}}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="2" style="padding-top: 0.35rem; border-top: 1px solid #ccc;">
                <strong>Totalt antall flasker</strong>
              </td>
              <td style="text-align: right; border-top: 1px solid #ccc;">
                <strong>{{ customer.TotaltAntallFlasker }}</strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </section>
</template>

<script>
import ObjektService from '@/services/ObjektService'
import AuksjonService from '@/services/AuksjonService'
import TheAutoCompleteInput from '../../Inputs/TheAutoCompleteInput'
import TheLoader from '../../TheLoader.vue'

export default {
  name: 'KjoperrapportGenerator',
  $_veeValidate: {
    validator: 'new'
  },
  components: {
    TheAutoCompleteInput,
    TheLoader
  },
  data () {
    return {
      form: {
        code: null,
        minCount: 1
      },
      searchDone: false,
      loading: false,
      customers: []
    }
  },
  computed: {
    isValidForSubmit: function () {
      return this.form.code && this.form.minCount >= 1
    }
  },
  mounted: function () {
    var self = this

    this._onBeforePrint = function () {
      var el = document.getElementById('kjoperrapport-generator')
      if (!el) return

      self._printParent = el.parentNode
      self._printNext = el.nextSibling

      var wrapper = document.createElement('div')
      wrapper.id = '__print-root__'
      wrapper.appendChild(el)
      document.body.insertBefore(wrapper, document.body.firstChild)

      var style = document.createElement('style')
      style.id = '__print-styles__'
      style.textContent =
        '@media print {' +
        ' body>*:not(#__print-root__){display:none!important}' +
        ' #__print-root__{display:block!important}' +
        ' .content-container{display:block!important}' +
        ' .customer-block{display:block!important;page-break-before:always!important;break-before:page!important}' +
        ' .customer-block:first-child{page-break-before:auto!important;break-before:auto!important}' +
        ' .hide-for-print{display:none!important}' +
        '}'
      document.head.appendChild(style)
    }

    this._onAfterPrint = function () {
      var el = document.getElementById('kjoperrapport-generator')
      if (el && self._printParent) {
        if (self._printNext) {
          self._printParent.insertBefore(el, self._printNext)
        } else {
          self._printParent.appendChild(el)
        }
      }
      var wrapper = document.getElementById('__print-root__')
      if (wrapper && wrapper.parentNode) wrapper.parentNode.removeChild(wrapper)
      var style = document.getElementById('__print-styles__')
      if (style && style.parentNode) style.parentNode.removeChild(style)
      self._printParent = null
      self._printNext = null
    }

    window.addEventListener('beforeprint', this._onBeforePrint)
    window.addEventListener('afterprint', this._onAfterPrint)
  },
  beforeDestroy: function () {
    window.removeEventListener('beforeprint', this._onBeforePrint)
    window.removeEventListener('afterprint', this._onAfterPrint)
    this._onAfterPrint()
  },
  methods: {
    handleSubmit: function () {
      if (!this.isValidForSubmit) {
        return
      }

      this.searchDone = false
      this.loading = true
      this.customers = []

      ObjektService.getKjoperrapport({
        code: this.form.code,
        minCount: this.form.minCount
      })
        .then(response => {
          this.customers = response.data.customers || []
        })
        .catch(error => {
          console.log('Error', error)
          this.$helpers.showErrorAlert('Feil', 'Noe gikk galt, prøv igjen', 'danger')
        })
        .then(() => {
          this.searchDone = true
          this.loading = false
        })
    },
    onAuksjonSearchInput: function (query) {
      return AuksjonService.searchInSettings(query, 2)
    },
    onAuksjonSearchSelected: function (item) {
      if (item) {
        this.form.code = item.Auksjon || null
      }
    },
    incrementCount: function () {
      this.form.minCount = (this.form.minCount || 1) + 1
    },
    decrementCount: function () {
      if (this.form.minCount > 1) {
        this.form.minCount -= 1
      }
    },
    clampCount: function () {
      if (!this.form.minCount || this.form.minCount < 1) {
        this.form.minCount = 1
      }
    },
    handlePrint: function () {
      window.print()
    }
  }
}
</script>

<style scoped>
.customer-block table thead th {
  background: #e8e8e8;
}

.select-container input[type="number"] {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0.35rem 2rem 0.45rem 0.6rem;
  border: 1px solid #cacaca;
  border-bottom: 1px solid rgba(0,0,0,0.15);
  border-radius: 3px;
  font-size: 0.75rem;
  background: #fff;
  box-shadow: inset 0 0 0 2px rgba(255,255,255,0.01), 0 1px 1px 0 rgba(0,0,0,0.05);
  -moz-appearance: textfield;
}
.select-container input[type="number"]::-webkit-outer-spin-button,
.select-container input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
.select-container input[type="number"]:focus {
  outline: none;
  border-color: rgba(51, 102, 204, 0.4);
  box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.12);
}

.glyph.stepper-arrows {
  pointer-events: all;
  width: 36px;
  height: 36px;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
}
.glyph.stepper-arrows svg {
  width: 44px;
  height: 44px;
  fill: #333;
  position: relative;
  top: -4px;
  left: -4px;
  pointer-events: none;
}
.glyph.stepper-arrows button {
  position: absolute;
  left: 0;
  width: 100%;
  height: 50%;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}
.glyph.stepper-arrows .stepper-up {
  top: 0;
}
.glyph.stepper-arrows .stepper-down {
  bottom: 0;
}
.glyph.stepper-arrows button:disabled {
  cursor: not-allowed;
}
.glyph.stepper-arrows:hover svg {
  fill: #3366cc;
}

@media print {
  .hide-for-print {
    display: none !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Admin/Reports/KjoperrapportGenerator.vue